import React, { useState, useEffect } from 'react';
import { appRepository, DiscoveredWord } from '../../core/storage/repository';
import { speechProvider } from '../../core/speech/speechProvider';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { BackIcon, BookIcon, CameraIcon, SpeakerIcon } from '../../components/icons/ReadingIcons';

interface MyWordsViewProps {
  onBack: () => void;
  onOpenCamera: () => void;
}

export const MyWordsView: React.FC<MyWordsViewProps> = ({ onBack, onOpenCamera }) => {
  const [words, setWords] = useState<DiscoveredWord[]>([]);
  const [mascotMessage, setMascotMessage] = useState('Ini kumpulan kata yang sudah kamu temukan!');

  useEffect(() => {
    setWords(appRepository.getState().discoveredWords);
    const unsubscribe = appRepository.subscribe(state => {
      setWords(state.discoveredWords);
    });
    return unsubscribe;
  }, []);

  const handleSpeak = (word: DiscoveredWord) => {
    setMascotMessage(`Membaca kata: ${word.originalText}!`);
    speechProvider.speakSyllablesAndWord(
      word.syllables.map(s => s.toLowerCase()),
      word.originalText
    );
  };

  const handleDelete = (id: string, text: string) => {
    if (window.confirm(`Hapus kata "${text}" dari Kataku?`)) {
      appRepository.deleteDiscoveredWord(id);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition"
        >
          <BackIcon className="w-6 h-6" />
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-teal-600 flex items-center gap-2">
          <BookIcon className="w-9 h-9" /> Kataku ({words.length})
        </h1>

        <button
          onClick={onOpenCamera}
          className="flex items-center gap-1.5 bg-[#FFB84D] hover:bg-amber-500 active:scale-95 text-amber-950 font-bold text-xs md:text-sm px-3.5 py-2 rounded-2xl shadow"
        >
          <CameraIcon className="w-6 h-6" /> Cari Lagi
        </button>
      </div>

      <div className="max-w-4xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-6">
        <RaraMascot
          state="happy"
          message={mascotMessage}
          size="sm"
        />

        {words.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-teal-200 text-center flex flex-col items-center">
            <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center text-5xl mb-4 border-2 border-teal-200">
              <BookIcon className="w-14 h-14" />
            </div>
            <h2 className="text-2xl font-display font-black text-[#243047]">
              Belum Ada Kata Tersimpan
            </h2>
            <p className="text-sm text-gray-600 max-w-sm mt-2">
              Yuk arahkan kamera ke tulisan di sekitarmu, lalu simpan kata-kata baru ke sini!
            </p>
            <button
              onClick={onOpenCamera}
              className="mt-6 px-8 py-3.5 bg-[#FFB84D] hover:bg-amber-500 text-amber-950 font-bold text-lg rounded-2xl shadow-md active:scale-95 transition"
            >
              <CameraIcon className="w-7 h-7" /> Buka Kamera Sekarang
            </button>
          </div>
        ) : (
          /* Words Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {words.map(w => (
              <div
                key={w.id}
                className="bg-white rounded-3xl p-5 shadow-md border-3 border-teal-200 hover:border-teal-400 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 border border-teal-200 flex items-center gap-1">
                      {w.source === 'camera' ? '📷 Kamera' : '📖 Pelajaran'}
                    </span>
                    <button
                      onClick={() => handleDelete(w.id, w.originalText)}
                      className="text-gray-400 hover:text-red-500 text-sm p-1 rounded-lg"
                      title="Hapus Kata"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Word Display */}
                  <div className="text-2xl md:text-3xl font-display font-black text-[#243047] mb-2 tracking-wide">
                    {w.originalText}
                  </div>

                  {/* Syllable Breakdown Chips */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {w.syllables.map((syl, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-amber-50 border border-amber-300 text-amber-900 font-display font-extrabold text-sm rounded-lg"
                      >
                        {syl}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleSpeak(w)}
                    className="flex-1 py-2.5 bg-teal-50 hover:bg-teal-100 active:scale-95 text-teal-800 font-bold text-sm rounded-xl flex items-center justify-center gap-1.5 transition"
                  >
                    <SpeakerIcon className="w-5 h-5" />
                    <span>Dengar</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
